import { Fragment } from 'react';

import {
  ActivityChangeOptions,
  ActivityItem,
  ActivityTagItem,
  diffItems,
} from '@spotify/plugin-confidence-activity-react';

import { pluralize, shouldSkipDetailedRender } from './utils/extensionHelpers';
import { createWorkflowUpdatesChangeExtension } from './workflow-updates-change';

interface Treatment {
  name?: string;
  weight: number;
  variant?: {
    name: string;
  };
  displayName?: string;
  description?: string;
  imageUri?: string;
}

interface TreatmentsData {
  treatments?: Treatment[];
}

const getTreatmentKey = (t: Treatment) => t.displayName || t.name || '';

const TreatmentChangeRender = ({ change }: ActivityChangeOptions) => {
  const oldValue = change.oldValue as TreatmentsData | null;
  const newValue = change.newValue as TreatmentsData | null;
  const oldTreatments = oldValue?.treatments ?? [];
  const newTreatments = newValue?.treatments ?? [];

  const { added: addedTreatments, removed: removedTreatments } = diffItems(
    oldTreatments,
    newTreatments,
    getTreatmentKey,
  );

  const weightChanges = newTreatments
    .map(newT => {
      const oldT = oldTreatments.find(
        t => (t.displayName || t.name) === (newT.displayName || newT.name),
      );

      if (oldT && oldT.weight !== newT.weight) {
        return {
          name: newT.displayName || newT.name || '',
          oldWeight: oldT.weight,
          newWeight: newT.weight,
        };
      }
      return null;
    })
    .filter(Boolean);

  // Skip rendering when tag shows single-item details
  if (
    shouldSkipDetailedRender(addedTreatments.length, removedTreatments.length)
  ) {
    return null;
  }

  return (
    <Fragment>
      {addedTreatments.length > 0 && (
        <ActivityItem.Section
          label={`Added ${addedTreatments.length} ${pluralize(addedTreatments.length, 'treatment')}`}
        >
          {addedTreatments.map(treatment => (
            <ActivityTagItem
              key={getTreatmentKey(treatment)}
              tagLabel={treatment.displayName || treatment.name || ''}
              newValue={`(${treatment.weight / 100}%)`}
              ariaLabel="Treatment name"
            />
          ))}
        </ActivityItem.Section>
      )}

      {removedTreatments.length > 0 && (
        <ActivityItem.Section
          label={`Removed ${removedTreatments.length} ${pluralize(removedTreatments.length, 'treatment')}`}
        >
          {removedTreatments.map(treatment => (
            <ActivityTagItem
              key={getTreatmentKey(treatment)}
              tagLabel={treatment.displayName || treatment.name || ''}
              ariaLabel="Treatment name"
            />
          ))}
        </ActivityItem.Section>
      )}

      {weightChanges.length > 0 && (
        <ActivityItem.Section
          label={`Updated ${weightChanges.length} ${pluralize(weightChanges.length, 'treatment')} weight`}
        >
          {weightChanges.map(newChange => (
            <ActivityTagItem
              key={newChange!.name}
              tagLabel={newChange!.name}
              oldValue={`${newChange!.oldWeight / 100}%`}
              newValue={`${newChange!.newWeight / 100}%`}
              ariaLabel="Treatment name"
            />
          ))}
        </ActivityItem.Section>
      )}
    </Fragment>
  );
};

export interface CreateTreatmentsChangeExtensionOptions {
  id: string;
  fieldPath: string;
}

export const createTreatmentsChangeExtension = (
  options: CreateTreatmentsChangeExtensionOptions,
) => {
  return createWorkflowUpdatesChangeExtension({
    id: options.id,
    predicate: ({ change }) => change.fieldPath === options.fieldPath,
    title: change => {
      const oldValue = change.oldValue as TreatmentsData | null;
      const newValue = change.newValue as TreatmentsData | null;
      const oldTreatments = oldValue?.treatments ?? [];
      const newTreatments = newValue?.treatments ?? [];

      const { added, removed } = diffItems(
        oldTreatments,
        newTreatments,
        getTreatmentKey,
      );

      if (added.length === 1 && removed.length === 0) {
        return 'Added treatment';
      }

      if (removed.length === 1 && added.length === 0) {
        return 'Removed treatment';
      }

      // Check for weight changes when no treatments were added/removed
      if (added.length === 0 && removed.length === 0) {
        const changedTreatments = newTreatments.filter(newT => {
          const oldT = oldTreatments.find(
            t => (t.displayName || t.name) === (newT.displayName || newT.name),
          );
          return oldT && oldT.weight !== newT.weight;
        });

        if (changedTreatments.length === 1) {
          return 'Updated';
        }
      }

      if (newTreatments.length > 0) {
        return `Updated ${newTreatments.length} ${pluralize(newTreatments.length, 'treatment')}`;
      }

      return 'Updated treatment options';
    },
    tag: change => {
      const oldValue = change.oldValue as TreatmentsData | null;
      const newValue = change.newValue as TreatmentsData | null;
      const oldTreatments = oldValue?.treatments ?? [];
      const newTreatments = newValue?.treatments ?? [];

      const { added, removed } = diffItems(
        oldTreatments,
        newTreatments,
        getTreatmentKey,
      );

      if (added.length === 1 && removed.length === 0) {
        return {
          tagLabel: getTreatmentKey(added[0]),
          ariaLabel: 'Treatment name',
        };
      }

      if (removed.length === 1 && added.length === 0) {
        return {
          tagLabel: getTreatmentKey(removed[0]),
          ariaLabel: 'Treatment name',
        };
      }

      // Check for weight changes when no treatments were added/removed
      if (added.length === 0 && removed.length === 0) {
        const changedTreatments = newTreatments.filter(newT => {
          const oldT = oldTreatments.find(
            t => (t.displayName || t.name) === (newT.displayName || newT.name),
          );
          return oldT && oldT.weight !== newT.weight;
        });

        if (changedTreatments.length === 1) {
          return {
            tagLabel: getTreatmentKey(changedTreatments[0]),
            ariaLabel: 'Treatment name',
          };
        }
      }

      return null;
    },
    render: TreatmentChangeRender,
  });
};
